import { ClientOngoingLoad, ClientUser, CrossWeekLoadAttributionOptions, PayrollPeriod, anchorWeekStartsOn, timeZonePreference } from '@numeo/types-core'
import { getLoadWeekAnchorTime } from '@numeo/types-main-web-app'
import { format } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { DateTime, Zone } from 'luxon'
import { WeekInterval } from '../types'

export type DateTimeRange = { start: DateTime; end: DateTime }

export const schedulerTimeZones = {
    UTC: 'UTC',
    ET: 'America/New_York', // Eastern Time
    CT: 'America/Chicago', // Central Time
    MT: 'America/Denver', // Mountain Time
    PT: 'America/Los_Angeles', // Pacific Time
} as const

export const userTimezone = (timezonePreference?: timeZonePreference) => {
    const tzPreference = timezonePreference || timeZonePreference.ET
    return schedulerTimeZones[tzPreference]
}

/**
 * Get the sort order for a driver based on their driver status.
 *
 * @param driver - The driver to get the sort order for.
 * @param weekRange - The range of dates to consider for the driver's status.
 * @param sortingOrder - The sorting order for the driver status.
 * @returns The sort order for the driver.
 */
export function getDriverSortOrder(driver: ClientUser, weekRange: { start: Date; end: Date }, sortingOrder: { statusId: string; order: number }[]): number {
    const statuses = Array.isArray(driver.driverStatus) ? driver.driverStatus : driver.driverStatus ? [driver.driverStatus] : []
    const overlapping = statuses.filter((ds) => {
        const start = ds.startsAt ? new Date(ds.startsAt) : new Date(0) // Far past
        const flag1 = start <= weekRange.end
        const flag2 = ds.endsAt ? new Date(ds.endsAt) >= weekRange.start : true
        return flag1 && flag2
    })

    if (overlapping.length === 0) return Infinity

    // Sort overlapping by earliest startsAt, using 0 if undefined
    overlapping.sort((a, b) => {
        const aStart = a.startsAt ? new Date(a.startsAt).getTime() : 0
        const bStart = b.startsAt ? new Date(b.startsAt).getTime() : 0
        return aStart - bStart
    })

    // Take the first one (earliest)
    const first = overlapping[0]
    const orderEntry = sortingOrder.find((s) => s.statusId === first.statusId)
    return orderEntry ? orderEntry.order : Infinity
}

export const inputSytle = {
    mb: 0.25,
    '--Input-minHeight': '28px',
    '--Input-paddingInline': '8px',
    '--Input-decoratorChildHeight': '20px',
    '& input': { fontSize: 12, lineHeight: 1.2, paddingBlock: '2px' },
    backgroundColor: 'background.level1',
}

export const getOverlapWithWeek = (interval: WeekInterval | null, weekDays: DateTime[]): { start: Date; end: Date; duration: number } | null => {
    if (!interval) return null

    const weekStart = weekDays[0].startOf('day')
    const weekEnd = weekDays[weekDays.length - 1].endOf('day')

    const intervalStart = DateTime.fromJSDate(interval.start).setZone(weekStart.zone)
    const intervalEnd = interval.end ? DateTime.fromJSDate(interval.end).setZone(weekStart.zone) : null

    if (intervalStart > weekEnd || (intervalEnd && intervalEnd < weekStart)) {
        return null
    }

    const overlapStart = DateTime.max(weekStart, intervalStart)
    const overlapEnd = intervalEnd ? DateTime.min(weekEnd, intervalEnd) : weekEnd

    return {
        start: overlapStart.toJSDate(),
        end: overlapEnd.toJSDate(),
        duration: overlapEnd.diff(overlapStart).as('milliseconds'),
    }
}

/**
 * Does this load belong to the given range?
 *
 * A load is anchored to ONE instant — its pickup or its delivery, per `crossWeekLoadAttribution` —
 * so it is paid in exactly one week even when the trip spans a week boundary. (The calendar bars are
 * a separate, deliberately overlapping set; see `useLoadCalculations`.)
 *
 * Only the anchor the chosen mode needs is required. Demanding BOTH dates, as this once did, silently
 * zeroed the gross of any load with a pickup date but no delivery deadline — 25 loads / $46,430 on one
 * production tenant. When the preferred anchor is absent we fall back to the other end: approximating
 * the week beats dropping the money out of every week.
 *
 * Both bounds are INCLUSIVE. Callers pass `[startOf(day), endOf(day)]`, i.e. `00:00:00.000` to
 * `23:59:59.999`, so adjacent weeks still cannot both claim one instant — while the previous strict
 * comparison dropped a load landing exactly on a week boundary from BOTH neighbouring weeks.
 *
 * @param load - The load to place
 * @param start - Start of the range, inclusive
 * @param end - End of the range, inclusive
 * @param crossWeekLoadAttribution - Anchor on pickup (START, default) or delivery (END)
 * @returns true if the load is attributed to this range
 */
export const isLoadOverlappingRange = (
    load: ClientOngoingLoad,
    start: Date,
    end: Date,
    crossWeekLoadAttribution?: CrossWeekLoadAttributionOptions
): boolean => {
    const anchorTime = getLoadAttributionAnchorTime(load, crossWeekLoadAttribution)
    if (anchorTime === null) return false

    return anchorTime >= start.getTime() && anchorTime <= end.getTime()
}

/**
 * The single instant a load's money is anchored to, or null when no stop carries a usable date.
 *
 * Exported because the board needs the instant itself, not just "is it in this range": a load spanning a
 * week boundary draws a bar in BOTH weeks while its rate counts in exactly one, and the bar in the other
 * week has to be able to say WHICH week that is.
 *
 * The rule itself lives in `@numeo/types` (`getLoadWeekAnchorTime`) because the Dispatcher Leaderboard
 * places the same money into the same weeks on the server. This wrapper only maps the board's enum onto it.
 */
export const getLoadAttributionAnchorTime = (load: ClientOngoingLoad, crossWeekLoadAttribution?: CrossWeekLoadAttributionOptions): number | null =>
    getLoadWeekAnchorTime(load, crossWeekLoadAttribution === CrossWeekLoadAttributionOptions.END)

/**
 * The company's period start day as a LUXON weekday (1=Mon … 7=Sun).
 *
 * `anchorWeekStartsOn` answers in JS `getDay()` terms (0=Sun … 6=Sat), so Sunday needs remapping from 0 to
 * 7 — hence the `|| 7`. Every Luxon caller on the board goes through here rather than repeating that `|| 7`:
 * two copies of the mapping would let the grid and a week label disagree about where Sunday sits.
 */
export const anchorWeekday = (anchor: PayrollPeriod = PayrollPeriod.MONDAY_SUNDAY): number => anchorWeekStartsOn(anchor) || 7

/**
 * The week an anchor instant is paid into, labelled by its first day ("Aug 3").
 *
 * A load spanning a week boundary draws a bar in BOTH weeks while its rate counts in exactly one, and the
 * bar in the other week has to be able to name which. The first day is picked in the SAME zone the board
 * built its week from (`weekDays[0].zone`, ultimately `getWeekRangeInUserTimezone`), so the label always
 * names a week the user can actually navigate to — computing it in the local browser zone would name the
 * neighbouring week for anyone whose zone differs from their scheduler preference.
 *
 * It takes the company's period start day for the same reason: under a Tuesday anchor the board has no
 * Monday-starting week to navigate to, so a Monday label would name a week that does not exist on the board.
 */
export const weekLabelFor = (anchorTime: number, zone: Zone | string, anchor?: PayrollPeriod): string => {
    const anchorInTz = DateTime.fromMillis(anchorTime, { zone })
    return anchorInTz.minus({ days: (anchorInTz.weekday - anchorWeekday(anchor) + 7) % 7 }).toFormat('MMM d')
}

// Returns current date in the specified timezone
export function getCurrentDateInTimezone(tzPreference?: timeZonePreference): string {
    const timeZone = userTimezone(tzPreference)
    const result = format(utcToZonedTime(new Date(), timeZone), 'yyyy-MM-dd HH:mm:ss.SSS')
    return result
}

export function timeZoneToUtc(dateString: string, tzPreference?: timeZonePreference): string {
    const timeZone = userTimezone(tzPreference)
    const result = zonedTimeToUtc(new Date(dateString), timeZone)
    return result.toISOString()
}

const getTimezoneAbbreviation = (date: Date, timeZone: string): string => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(date)
    return parts.find((part) => part.type === 'timeZoneName')?.value || ''
}

export function utcToTimezone(dateString: string, tzPreference?: timeZonePreference, displayFormat?: boolean): string {
    const timeZone = userTimezone(tzPreference)
    const zonedDate = utcToZonedTime(new Date(dateString), timeZone)
    if (displayFormat) {
        const dateStr = format(zonedDate, 'EEE, M/d')
        const timeStr = format(zonedDate, 'h:mm a')
        const tzAbbr = getTimezoneAbbreviation(zonedDate, timeZone)
        return `${dateStr} at ${timeStr} ${tzAbbr}`
    }
    return format(zonedDate, 'yyyy-MM-dd HH:mm:ss.SSS')
}

export const normalizeDateForInput = (dateStr: string) => {
    if (!dateStr) return ''
    if (dateStr.includes('/')) {
        const [m, d, y] = dateStr.split('/')
        if (y && y.length === 4) return `${y}-${m}-${d}`
    }
    return dateStr
}
